// @flow

import { connect } from 'react-redux';

import {
  type ChangeType,
  type Interval,
  changeTypes,
} from '@zapier/billing/src/types';

import * as Account from 'app/entities/Account';
import * as Plan from 'app/entities/Plan';
import BillingActions from 'app/billing/BillingActions';
import ChangePlanCompleteModal, {
  type Props as ChangePlanCompleteModalProps,
} from 'app/billing/components/ChangePlanCompleteModal';
import { attachAccountIdToUrl } from 'app/common/middlewares/accountSwitcherMiddleware';
import { selectPausedByPlanZaps } from 'app/beta-editor/Selectors';

import { type HOC } from 'recompose';

import { type Dispatch } from 'app/common/types';

const getPrimaryButtonProps = ({
  hasPausedZaps,
  isNewPlanCompany,
  isNewPlanTeams,
  isUpgrade,
  newestAccountId,
}) => {
  if (isNewPlanCompany) {
    return {
      primaryButtonHref: '/help/account/companies/zapier-companies',
      primaryButtonText: 'Start Setup',
    };
  }
  if (newestAccountId) {
    return {
      primaryButtonHref: attachAccountIdToUrl(newestAccountId, '/app/zaps'),
      primaryButtonText: 'Go to New Account Dashboard',
    };
  }
  if (isNewPlanTeams) {
    return {
      primaryButtonHref: '/app/settings/team',
      primaryButtonText: 'Invite Team Members',
    };
  }
  if (hasPausedZaps) {
    return {
      primaryButtonHref: '/app/settings/billing/zaps',
      primaryButtonText: isUpgrade ? 'Resume Paused Zaps' : 'View Paused Zaps',
    };
  }
  return {
    primaryButtonHref: '/app/zaps',
    primaryButtonText: 'Go to My Zaps',
  };
};

const getSecondaryButtonProps = ({
  hasPausedZaps,
  isNewPlanCompany,
  isNewPlanTeams,
  newestAccountId,
}) => {
  if (isNewPlanCompany) {
    return {
      secondaryButtonHref: '/app/dashboard',
      secondaryButtonText: 'Go to Dashboard',
    };
  }
  if (newestAccountId && isNewPlanTeams) {
    return {
      secondaryButtonHref: attachAccountIdToUrl(
        newestAccountId,
        '/app/settings/team'
      ),
      secondaryButtonText: 'Manage New Team',
    };
  }
  if (isNewPlanTeams) {
    return {
      secondaryButtonHref: '/app/zaps',
      secondaryButtonText: 'Go to Team Zaps',
    };
  }
  if (!hasPausedZaps) {
    return {
      secondaryButtonHref: '/app/billing/plans',
      secondaryButtonText: '← Plans and Pricing',
    };
  }
  return null;
};

const buildContent = ({
  changeType,
  isCreatingNewAccount,
  isFree,
  planInterval,
  planName,
}: {
  changeType: ChangeType,
  isCreatingNewAccount: boolean,
  isFree: boolean,
  planInterval: Interval,
  planName: string,
}) => {
  const planStatement = isCreatingNewAccount
    ? `You just created a new account on the ${planName} plan`
    : `You’re now on the ${planName} plan`;
  const payingStatement = isFree ? '' : `paying ${planInterval}ly`;
  const punctuation = changeType === changeTypes.upgrade ? '!' : '.';
  return `${planStatement} ${payingStatement}${punctuation}`.replace(
    ` ${punctuation}`,
    punctuation
  );
};

type OwnProps = {
  changeType: ChangeType,
  isCreatingNewAccount?: boolean,
  toPlanId: number,
};

const mapStateToProps = (
  state,
  { changeType, isCreatingNewAccount = false, toPlanId }: OwnProps
) => {
  const newPlan = Plan.selectors.all.entity(toPlanId, state);
  const newestAccountId =
    isCreatingNewAccount &&
    Account.selectors.id(Account.selectors.all.findNewestAccount(state));
  const isNewPlanTeams = Plan.selectors.hasTeams(newPlan);
  const isNewPlanCompany = Plan.selectors.isCompaniesPlan(newPlan);
  const hasPausedZaps = selectPausedByPlanZaps(state).length > 0;
  const isUpgrade = changeType === changeTypes.upgrade;

  return {
    content: isNewPlanCompany
      ? 'You just created a Company -- well done!'
      : isNewPlanTeams
      ? 'Well done, you just created a team!'
      : buildContent({
          changeType,
          isFree: Plan.selectors.isFree(newPlan),
          isCreatingNewAccount,
          planInterval: Plan.selectors.interval(newPlan),
          planName: Plan.selectors.name(newPlan),
        }),
    heading: isCreatingNewAccount
      ? 'Thanks for creating a new account!'
      : changeType === changeTypes.change
      ? 'Change Complete'
      : isUpgrade
      ? 'Thanks for upgrading!'
      : 'Downgrade Complete',
    imgSrc: isUpgrade
      ? 'images/applings/zapier-celebrate.svg'
      : 'images/applings/zapier-wave.svg',
    isPartying: isUpgrade,
    ...getPrimaryButtonProps({
      hasPausedZaps,
      isNewPlanTeams,
      isNewPlanCompany,
      isUpgrade,
      newestAccountId,
    }),
    ...getSecondaryButtonProps({
      hasPausedZaps,
      isNewPlanCompany,
      isNewPlanTeams,
      newestAccountId,
    }),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onLeave() {
    dispatch(BillingActions.onLeave());
  },
});

const connector: HOC<ChangePlanCompleteModalProps, OwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
);

export default connector(ChangePlanCompleteModal);
